<template>
  <BaseLayout>
    <template v-slot:top_buttons>
      <div v-if="activeTab === 'list'" class="demo-inline-spacing">
        <button type="button" class="btn btn-primary" @click="createFromDocument">
          Загрузить документ
        </button>
        <button type="button" class="btn btn-outline-primary" @click="createFromLinks">
          Загрузить по ссылкам
        </button>
        <button type="button" class="btn btn-outline-primary" @click="importFromFile">
          Импорт из файла ВИ
        </button>
        <button type="button" class="btn btn-outline-primary" @click="createManual">
          Создать вручную
        </button>
        <button v-if="hasOtherCompanies" type="button" class="btn btn-outline-secondary" @click="copyToCompany">
          Скопировать в другую компанию
        </button>
      </div>
    </template>

    <BaseModal ref="documentModal" modal-title="Загрузка документа">
      <CertificateFromDocumentForm ref="documentForm" @success="documentSuccess"/>
    </BaseModal>

    <BaseModal ref="linksModal" modal-title="Загрузка по ссылкам" :close-on-backdrop="false">
      <CertificateFromLinksForm ref="linksForm"/>
    </BaseModal>

    <BaseModal ref="importModal" modal-title="Импорт из файла ВИ">
      <CertificateImportFileForm ref="importForm"/>
    </BaseModal>

    <BaseModal ref="copyModal" modal-title="Копирование сертификатов в другую компанию">
      <CertificateCopyToCompanyForm ref="copyForm"/>
    </BaseModal>

    <BaseModal ref="itemModal" :modal-title="modalTitle" :close-on-backdrop="false">
      <CertificateForm ref="itemForm" @success="formSuccess"/>
    </BaseModal>

    <ul class="nav nav-tabs certificate-tabs mb-4">
      <li class="nav-item">
        <a href="#" class="nav-link" :class="{ active: activeTab === 'list' }"
           @click.prevent="setTab('list')">Список</a>
      </li>
      <li class="nav-item">
        <a href="#" class="nav-link" :class="{ active: activeTab === 'auto-link' }"
           @click.prevent="setTab('auto-link')">Сопоставление с товарами</a>
      </li>
      <li class="nav-item">
        <a href="#" class="nav-link" :class="{ active: activeTab === 'ozon' }"
           @click.prevent="setTab('ozon')">Озон</a>
      </li>
    </ul>

    <div v-if="activeTab === 'list'" class="card">
      <div class="card-body">
        <div class="row gy-sm-1">
          <div class="col-md-3">
            <input type="text" class="form-control border-1 py-2" placeholder="Номер"
                   v-model="searchData.number" @change="search">
          </div>
          <div class="col-md-3">
            <Select v-model="searchData.kind" :options="kindOptions" optionLabel="name" optionValue="code"
                    placeholder="Вид" showClear style="width: 100%" @change="search"/>
          </div>
          <div class="col-md-3">
            <Select v-model="searchData.compliance_type" :options="complianceOptions" optionLabel="name"
                    optionValue="code" placeholder="Тип соответствия" showClear style="width: 100%"
                    @change="search"/>
          </div>
          <div class="col-md-3">
            <input type="text" class="form-control border-1 py-2" placeholder="Бренд"
                   v-model="searchData.brand" @change="search">
          </div>
          <div class="col-md-3">
            <input type="text" class="form-control border-1 py-2" placeholder="Артикул"
                   v-model="searchData.article" @change="search">
          </div>
          <div class="col-md-3">
            <input type="text" class="form-control border-1 py-2" placeholder="ТНВЭД"
                   v-model="searchData.tnved" @change="search">
          </div>
        </div>
        <div v-if="!items.length && !loading" class="mt-7">
          Сертификаты не найдены
        </div>
      </div>
      <LoadingBlock :loading="loading">
        <div class="table-responsive text-nowrap">
          <table class="table">
            <thead>
            <tr>
              <th>Номер</th>
              <th>Вид</th>
              <th>Тип соответствия</th>
              <th>Дата выдачи</th>
              <th>Срок действия</th>
              <th>Бренды</th>
              <th>Документ</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>
                <a href="#" @click.prevent="openDetail(item.id)">{{ item.number }}</a>
              </td>
              <td>{{ getKindLabel(item.kind) }}</td>
              <td>{{ getComplianceLabel(item.compliance_type) }}</td>
              <td>{{ formatCertificateDate(item.issue_date) }}</td>
              <td>{{ item.is_perpetual ? 'Бессрочный' : formatCertificateDate(item.valid_until) }}</td>
              <td>{{ formatTags(item.brands) }}</td>
              <td>
                <a v-if="item.document?.file_url" :href="item.document.file_url" target="_blank"
                   rel="noopener noreferrer">
                  {{ item.document.file_name || 'Скачать' }}
                </a>
                <a v-else-if="item.registry_urls?.length" :href="item.registry_urls[0]" target="_blank"
                   rel="noopener noreferrer">
                  Открыть в реестре
                </a>
                <span v-else>—</span>
              </td>
            </tr>
            </tbody>
          </table>

          <BasePagination :info="paginationInfo" @changePage="changePage"/>
        </div>
      </LoadingBlock>
    </div>

    <CertificateAutoLink v-else-if="activeTab === 'auto-link'"/>
    <CertificateOzon v-else/>
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/layouts/Base.vue'
import {PageInfo} from "@/data/page";
import {user} from "@/data/user";
import BaseModal from "@/components/Modal.vue";
import BasePagination from "@/components/list/Pagination.vue"
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import {getList} from "@/api/certificate";
import CertificateForm from "@/components/forms/Certificate.vue";
import CertificateFromDocumentForm from "@/components/forms/CertificateFromDocument.vue";
import CertificateFromLinksForm from "@/components/forms/CertificateFromLinks.vue";
import CertificateImportFileForm from "@/components/forms/CertificateImportFile.vue";
import CertificateCopyToCompanyForm from "@/components/forms/CertificateCopyToCompany.vue";
import CertificateAutoLink from "@/components/certificate/CertificateAutoLink.vue";
import CertificateOzon from "@/components/certificate/CertificateOzon.vue";
import {Select} from "primevue";
import {
    certificateKinds,
    complianceTypes,
    formatCertificateDate,
    formatTags,
    getComplianceLabel,
    getKindLabel,
} from "@/data/certificate";

export default {
  name: 'CertificatesView',
  components: {
    CertificateOzon,
    CertificateAutoLink,
    CertificateFromDocumentForm,
    CertificateFromLinksForm,
    CertificateImportFileForm,
    CertificateCopyToCompanyForm,
    CertificateForm,
    LoadingBlock,
    BaseModal,
    BaseLayout,
    BasePagination,
    Select,
  },
  data() {
    return {
      items: [],
      loading: true,
      modalTitle: 'Новый сертификат',
      paginationInfo: {},
      kindOptions: certificateKinds,
      complianceOptions: complianceTypes,
      searchData: {
        number: '',
        kind: null,
        compliance_type: null,
        brand: '',
        article: '',
        tnved: '',
      }
    }
  },
  computed: {
    hasOtherCompanies() {
      return user.companies.length > 1
    },
    activeTab() {
      if (this.$route.query.tab === 'auto-link' || this.$route.query.tab === 'ozon') {
        return this.$route.query.tab
      }
      return 'list'
    },
  },
  methods: {
    getKindLabel,
    getComplianceLabel,
    formatCertificateDate,
    formatTags,
    async setTab(tab) {
      const query = {...this.$route.query}
      if (tab === 'list') {
        delete query.tab
      } else {
        query.tab = tab
      }
      if (tab !== 'ozon') {
        delete query.ozon_store_id
        delete query.ozon_tab
      }
      delete query.page
      await this.$router.push({path: this.$route.path, query})
    },
    async getItems() {
      if (this.activeTab !== 'list') {
        this.loading = false
        return
      }
      this.loading = true
      const query = {...this.$route.query}
      delete query.tab
      delete query.ozon_store_id
      delete query.ozon_tab
      const result = await getList(user.active_company, query)
      this.paginationInfo = {
        total: result.data.total,
        pages: result.data.pages,
        page: result.data.page,
        size: result.data.size
      }
      this.items = result.data.items
      this.loading = false
    },
    async search() {
      const query = {...this.$route.query}
      delete query.page
      Object.entries(this.searchData).forEach(([key, value]) => {
        if (value) {
          query[key] = value
        } else {
          delete query[key]
        }
      });
      await this.$router.push({
        path: this.$route.path,
        query: {...query}
      })
    },
    setSearch() {
      const fields = ['number', 'kind', 'compliance_type', 'brand', 'article', 'tnved']
      fields.forEach((field) => {
        this.searchData[field] = this.$route.query[field] || (field === 'kind' || field === 'compliance_type' ? null : '')
      })
    },
    createFromDocument() {
      setTimeout(() => {
        this.$refs.documentModal.openModal()
        this.$refs.documentForm.reset()
      }, 100)
    },
    createFromLinks() {
      // текст в поле сохраняется между открытиями окна, пока загрузка не запущена
      setTimeout(() => {
        this.$refs.linksModal.openModal()
      }, 100)
    },
    importFromFile() {
      setTimeout(() => {
        this.$refs.importModal.openModal()
        this.$refs.importForm.reset()
      }, 100)
    },
    copyToCompany() {
      setTimeout(() => {
        this.$refs.copyModal.openModal()
        this.$refs.copyForm.reset()
      }, 100)
    },
    createManual() {
      this.modalTitle = 'Новый сертификат'
      setTimeout(() => {
        this.$refs.itemModal.openModal()
        this.$refs.itemForm.startNew()
      }, 100)
    },
    openDetail(id) {
      this.$router.push({name: 'certificate-detail', params: {id}})
    },
    async changePage(page) {
      await this.$router.push({
        path: this.$route.path,
        query: {...this.$route.query, page}
      });
    },
    setPageInfo() {
      PageInfo.title = 'Сертификаты'
      PageInfo.breadcrumbs = [
        {title: 'Главная', link: '/'},
        {title: PageInfo.title}
      ]
    },
    async formSuccess(data) {
      this.$refs.itemModal.closeModal()
      if (data?.id && !this.$route.params.id) {
        await this.$router.push({name: 'certificate-detail', params: {id: data.id}})
      } else {
        await this.getItems()
      }
    },
    documentSuccess(certificate) {
      this.$refs.documentModal.closeModal()
      if (certificate?.id) {
        this.$router.push({name: 'certificate-detail', params: {id: certificate.id}})
      } else {
        this.getItems()
      }
    },
  },
  async mounted() {
    this.setPageInfo()
    this.setSearch()
    await user.getUserBase()
    try {
      await user.getUserCompanies()
    } catch (e) {
      // без списка компаний просто не покажем кнопку копирования
    }
    await this.getItems()
  },
  watch: {
    '$route'() {
      this.setSearch()
      this.setPageInfo()
      this.getItems()
    }
  }
}
</script>

<style scoped>
.certificate-tabs .nav-link {
  text-transform: none;
}
</style>
